import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  AriaLiveRegion,
  Button,
  focusTrap,
  getTranslation,
  Image,
  Modal,
} from '@resy/ui-kitchen';
import AddIcon from '../../../../../ui-kitchen/src/svg/icons/ic_add_20x20.svg';
import CloseThick from '../../svg/dynamic/ic_close_20x20.svg';
import inFrame from '../../utils/inFrame';
import {
  IYP_MODAL_AVATAR_ACTION_NAME,
  getIypDismissalMethod,
  getIypModalInviteContext,
  IYP_MODAL_INVITE_ACTION_NAME,
  IYP_MODAL_AVATAR_BUTTON_ID,
  IYP_MODAL_CLOSE_DELAY_MS,
  IYP_MODAL_DELAY_MS,
  isEligibleForIypModal,
} from './reservationQuestionnaireInviteFlow';

function renderIypModalAvatar(reservation) {
  const host = reservation?.party?.find((guest) => guest.type === 'host');
  if (host?.profile_image_url) {
    return <Image src={host.profile_image_url} className="ReservationQuestionnaire__iyp-modal-avatar-img" />;
  }

  const firstInitial = host?.first_name?.trim()?.[0] || host?.label?.trim()?.[0] || 'R';
  const lastInitial = host?.last_name?.trim()?.[0]
    || host?.label?.trim()?.split(/\s+/)?.[1]?.[0]
    || '';

  return (
    <div className="ReservationQuestionnaire__iyp-modal-avatar-fallback">
      {`${firstInitial}${lastInitial}`.toUpperCase()}
    </div>
  );
}

export default function IypInviteModal({
  ariaLiveText,
  copyInviteLink,
  copyLinkActive,
  forceIsOpen,
  getPageContext,
  matchMedia,
  onDismiss,
  pageContexts,
  reservation,
  trackIypEvent,
}) {
  const [showIypModal, setShowIypModal] = useState(false);
  const [hasShownIypModalInFlow, setHasShownIypModalInFlow] = useState(false);
  const openTimerRef = useRef(null);
  const closeTimerRef = useRef(null);

  useEffect(() => {
    if (!reservation) {
      return undefined;
    }

    const eligible = isEligibleForIypModal({
      getPageContext,
      hasShownIypModalInFlow,
      hash: window.location.hash,
      inFrameCheck: inFrame,
      matchMedia,
      pageContexts,
      reservation,
    });

    if (!eligible) {
      return undefined;
    }

    openTimerRef.current = window.setTimeout(() => {
      setShowIypModal(true);
      setHasShownIypModalInFlow(true);
      trackIypEvent('iyp modal shown');
    }, IYP_MODAL_DELAY_MS);

    return () => {
      if (openTimerRef.current) {
        window.clearTimeout(openTimerRef.current);
      }
    };
  }, [getPageContext, hasShownIypModalInFlow, matchMedia, pageContexts, reservation, trackIypEvent]);

  useEffect(() => () => {
    if (openTimerRef.current) {
      window.clearTimeout(openTimerRef.current);
    }
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
    }
  }, []);

  const closeModal = (dismissalMethod) => {
    if (dismissalMethod) {
      trackIypEvent('iyp modal dismissed', { dismissal_method: dismissalMethod });
    }
    setShowIypModal(false);
    if (typeof forceIsOpen === 'boolean' && onDismiss) {
      onDismiss();
    }
  };

  const onInviteTap = async (tapTarget) => {
    const context = getIypModalInviteContext(tapTarget);
    const didCopy = await copyInviteLink(context);
    if (!didCopy) {
      return;
    }

    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
    }
    closeTimerRef.current = window.setTimeout(() => {
      closeModal();
    }, IYP_MODAL_CLOSE_DELAY_MS);
  };

  const isOpen = typeof forceIsOpen === 'boolean' ? forceIsOpen : showIypModal;

  if (!isOpen) {
    return null;
  }

  const onAfterOpen = () => {
    focusTrap.focusModal();
    window.requestAnimationFrame(() => {
      document.getElementById(IYP_MODAL_AVATAR_BUTTON_ID)?.focus();
    });
  };

  const iypModalAriaLabel = [
    getTranslation({ translationKey: 'reservation_header', fallback: 'Reservation Booked.' }),
    getTranslation({ translationKey: 'invite_who_you_re_dining_with', fallback: 'Invite who you’re dining with' }),
  ].join(' ').trim();

  return (
    <Modal
      ariaHideApp={false}
      className="ReactModal__Content--footer ReservationQuestionnaire__iyp-modal-sheet"
      closeTimeoutMS={260}
      isOpen={isOpen}
      onRequestClose={(event) => closeModal(getIypDismissalMethod(event))}
      shouldCloseOnEsc
      shouldCloseOnOverlayClick
      onAfterOpen={onAfterOpen}
      onAfterClose={focusTrap.focusPage}
      styles={{
        overlay: {
          alignItems: 'flex-end',
          backgroundColor: 'rgba(0, 0, 0, 0.38)',
          display: 'flex',
          zIndex: '10',
        },
        content: {
          backgroundColor: '#fff',
          border: '1px solid #cacaca',
          borderBottom: 'none',
          borderRadius: '12px 12px 0 0',
          bottom: 0,
          boxSizing: 'border-box',
          inset: 'auto 0 0',
          left: 0,
          margin: '0 auto',
          maxWidth: '640px',
          padding: '0',
          right: 0,
          top: 'auto',
          width: '100%',
        },
      }}
    >
      <div
        aria-label={iypModalAriaLabel}
        aria-modal="true"
        className="ReservationQuestionnaire__iyp-modal"
        data-testid="iyp-modal"
        role="dialog"
      >
        <div className="ReservationQuestionnaire__iyp-modal-nav">
          <div className="ReservationQuestionnaire__iyp-modal-nav-spacer" />
          <Button
            aria-label={getTranslation({ translationKey: 'close_invite_modal', fallback: 'Close invite modal' })}
            className="ReservationQuestionnaire__iyp-modal-close"
            data-testid="iyp-modal-dismiss-cta"
            onClick={() => closeModal('close_button')}
            variant="close"
          >
            <CloseThick />
          </Button>
        </div>
        <div className="ReservationQuestionnaire__iyp-modal-content">
          <Button
            aria-label={getTranslation({ translationKey: 'invite_who_you_re_dining_with', fallback: 'Invite who you’re dining with' })}
            className="ReservationQuestionnaire__iyp-modal-avatar-button"
            data-testid="iyp-avatar-cta"
            data-dd-action-name={IYP_MODAL_AVATAR_ACTION_NAME}
            id={IYP_MODAL_AVATAR_BUTTON_ID}
            onClick={() => onInviteTap('avatar_plus')}
            variant="no-style"
          >
            <div className="ReservationQuestionnaire__iyp-modal-avatar-wrap">
              {renderIypModalAvatar(reservation)}
              <span className="ReservationQuestionnaire__iyp-modal-plus">
                <AddIcon aria-hidden className="ReservationQuestionnaire__iyp-modal-plus-glyph" />
              </span>
            </div>
          </Button>
          <h2 className="ReservationQuestionnaire__iyp-modal-title" id="iyp-modal-title">
            {getTranslation({ translationKey: 'invite_who_you_re_dining_with', fallback: 'Invite who you’re dining with' })}
          </h2>
          <p className="ReservationQuestionnaire__iyp-modal-copy">
            {getTranslation({
              translationKey: 'invite_who_you_re_dining_with_copy',
              fallback: 'Your guests can view the reservation, get updates, and share any dietary needs before you arrive.',
            })}
          </p>
          <Button
            aria-label={copyLinkActive
              ? getTranslation({ translationKey: 'copied_invite_link', fallback: 'Copied invite link' })
              : getTranslation({ translationKey: 'invite', fallback: 'Invite' })}
            className="ReservationQuestionnaire__iyp-modal-invite"
            data-testid="iyp-invite-cta"
            data-dd-action-name={IYP_MODAL_INVITE_ACTION_NAME}
            onClick={() => onInviteTap('cta')}
            variant="primary"
          >
            {copyLinkActive
              ? getTranslation({ translationKey: 'button_copied_bang', fallback: 'Copied!' })
              : getTranslation({ translationKey: 'invite', fallback: 'Invite' })}
          </Button>
          {copyLinkActive && (
            <div className="ReservationQuestionnaire__iyp-modal-copy-success" role="status">
              {getTranslation({ translationKey: 'copied_invite_link', fallback: 'Copied invite link' })}
            </div>
          )}
          <AriaLiveRegion>
            {ariaLiveText}
          </AriaLiveRegion>
        </div>
      </div>
    </Modal>
  );
}

IypInviteModal.propTypes = {
  ariaLiveText: PropTypes.string,
  copyInviteLink: PropTypes.func.isRequired,
  copyLinkActive: PropTypes.bool.isRequired,
  forceIsOpen: PropTypes.bool,
  getPageContext: PropTypes.func.isRequired,
  matchMedia: PropTypes.shape({
    md: PropTypes.bool,
  }).isRequired,
  onDismiss: PropTypes.func,
  pageContexts: PropTypes.shape({
    POST_BOOKING: PropTypes.string.isRequired,
  }).isRequired,
  reservation: PropTypes.shape({}).isRequired,
  trackIypEvent: PropTypes.func.isRequired,
};

IypInviteModal.defaultProps = {
  ariaLiveText: null,
  forceIsOpen: undefined,
  onDismiss: undefined,
};
